import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useForm, useFieldArray } from 'react-hook-form';
import axios from 'axios';

const TimesheetFillPage = () => {
  const [searchParams] = useSearchParams();
  const payrollId = searchParams.get('payrollId');
  const cycleId = searchParams.get('cycleId');

  const [timesheet, setTimesheet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isDirty }
  } = useForm({
    defaultValues: {
      entries: []
    }
  });

  const { fields } = useFieldArray({
    control,
    name: "entries"
  });

  const watchedEntries = watch("entries");

  useEffect(() => {
    if (payrollId && cycleId) {
      axios
        .get(`${process.env.REACT_APP_BASE_URL}/timesheet/${payrollId}/${cycleId}`)
        .then((res) => {
          const data = res.data.data;
          setTimesheet(data);

          // Reset form with fetched data
          reset({
            entries: data.entries.map(entry => ({
              ...entry,
              workingHours: entry.workingHours || 0
            }))
          });

          setLoading(false);
        })
        .catch((err) => {
          console.error('Fetch error:', err);
          alert('Failed to load timesheet data');
          setLoading(false);
        });
    }
  }, [payrollId, cycleId, reset]);

  const onSubmit = (data) => {
    if (timesheet?.isCycleSubmitted) {
      alert('This timesheet has already been submitted and cannot be modified.');
      return;
    }
    setShowConfirmDialog(true);
  };

  const handleConfirmSubmit = async () => {
    setShowConfirmDialog(false);
    setSubmitLoading(true);

    try {
      // Only send entries that have been modified
      const modifiedEntries = watchedEntries.map((entry, index) => ({
        ...timesheet.entries[index],
        workingHours: Number(entry.workingHours)
      }));

      await axios.put(
        `${process.env.REACT_APP_BASE_URL}/timesheet/update/${payrollId}/${cycleId}`,
        {
          entries: modifiedEntries,
          isCycleSubmitted: true
        }
      );

      alert('Timesheet submitted successfully!');

      // Update local state to reflect submission
      setTimesheet(prev => ({ ...prev, isCycleSubmitted: true }));

    } catch (err) {
      console.error('Submit error:', err);
      alert('Failed to submit timesheet. Please try again.');
    } finally {
      setSubmitLoading(false);
    }
  };

  const getTotalHours = () => {
    return watchedEntries.reduce((total, entry) => total + (Number(entry.workingHours) || 0), 0);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-lg">Loading timesheet...</div>
      </div>
    );
  }

  if (!timesheet) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-lg text-red-600">Timesheet not found</div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white rounded-lg shadow-lg">
      <h1 className="text-3xl font-bold mb-4 text-gray-800">Fill Timesheet</h1>

      <div className="bg-gray-50 p-4 rounded-lg mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <strong className="text-gray-700">Name:</strong>
            <span className="ml-2">{timesheet.payrollId.name}</span>
          </div>
          <div>
            <strong className="text-gray-700">Email:</strong>
            <span className="ml-2">{timesheet.payrollId.email}</span>
          </div>
          <div>
            <strong className="text-gray-700">Cycle:</strong>
            <span className="ml-2">{timesheet.cycleId.CycleName}</span>
          </div>
        </div>
      </div>

      {timesheet.isCycleSubmitted && (
        <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-500" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm">
                <strong>Already Submitted:</strong> This timesheet has already been submitted and cannot be modified.
              </p>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="overflow-x-auto">
          <table className="w-full text-left border border-gray-300 border-collapse">
            <thead>
              <tr className="bg-gray-100">
                <th className="border border-gray-300 px-4 py-3 font-semibold">Date</th>
                <th className="border border-gray-300 px-4 py-3 font-semibold">Working Hours</th>
                <th className="border border-gray-300 px-4 py-3 font-semibold">Day</th>
              </tr>
            </thead>
            <tbody>
              {fields.map((field, index) => (
                <tr key={field.id} className="hover:bg-gray-50">
                  <td className="border border-gray-300 px-4 py-3">
                    {new Date(field.date).toLocaleDateString('en-US', {
                      weekday: 'short',
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric'
                    })}
                  </td>
                  <td className="border border-gray-300 px-4 py-3">
                    <input
                      type="number"
                      step="0.5"
                      min="0"
                      max="24"
                      disabled={timesheet.isCycleSubmitted}
                      {...control.register(`entries.${index}.workingHours`, {
                        required: 'Working hours is required',
                        min: { value: 0, message: 'Hours cannot be negative' },
                        max: { value: 24, message: 'Hours cannot exceed 24' },
                        valueAsNumber: true
                      })}
                      className={`w-20 p-2 border rounded-md ${timesheet.isCycleSubmitted
                          ? 'bg-gray-100 cursor-not-allowed'
                          : 'border-gray-300 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500'
                        } ${errors.entries?.[index]?.workingHours ? 'border-red-500' : ''}`}
                    />
                    {errors.entries?.[index]?.workingHours && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.entries[index].workingHours.message}
                      </p>
                    )}
                  </td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-600">
                    {new Date(field.date).toLocaleDateString('en-US', { weekday: 'long' })}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-gray-100 font-semibold">
                <td className="border border-gray-300 px-4 py-3">Total Hours:</td>
                <td className="border border-gray-300 px-4 py-3">
                  {getTotalHours()} hours
                </td>
                <td className="border border-gray-300 px-4 py-3"></td>
              </tr>
            </tfoot>
          </table>
        </div>

        {!timesheet.isCycleSubmitted && (
          <div className="flex justify-between items-center mt-6">
            <div className="text-sm text-gray-600">
              {isDirty ? 'You have unsaved changes' : 'No changes made'}
            </div>
            <button
              type="submit"
              disabled={submitLoading || !isDirty}
              className="bg-indigo-600 text-white px-6 py-3 rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 font-medium"
            >
              {submitLoading ? 'Submitting...' : 'Submit Timesheet'}
            </button>
          </div>
        )}
      </form>

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Confirm Submission</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to submit this timesheet? Once submitted, you won't be able to make any changes.
            </p>
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => setShowConfirmDialog(false)}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors duration-200"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmSubmit}
                disabled={submitLoading}
                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 transition-colors duration-200"
              >
                {submitLoading ? 'Submitting...' : 'Yes, Submit'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TimesheetFillPage;